import { Shield, AlertTriangle, TrendingUp, TrendingDown, LayoutGrid, ScatterChart, Network, Layers } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { ExportButton } from '@/components/ExportButton';
import { FilterChips } from '@/components/FilterChips';
import { RiskHeatMap } from '@/components/visualizations/RiskHeatMap';
import { LikelihoodImpactMatrix } from '@/components/visualizations/LikelihoodImpactMatrix';
import { RiskDependencyMap } from '@/components/visualizations/RiskDependencyMap';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useNavigation } from '@/contexts/NavigationContext';

type ViewMode = 'table' | 'heatmap' | 'matrix' | 'dependency';
type RiskTier = 'all' | '1' | '2' | '3';

export function RisksPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedTier, setSelectedTier] = useState<RiskTier>('all');
  const [activeFilters, setActiveFilters] = useState([
    { key: 'severity', label: 'Severity', value: 'Critical' },
    { key: 'department', label: 'Department', value: 'Technology' },
  ]);
  const { sectionColor } = useNavigation();

  const handleRemoveFilter = (filterKey: string) => {
    setActiveFilters(activeFilters.filter(f => f.key !== filterKey));
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
  };

  const handleExport = (format: 'pdf' | 'csv' | 'excel') => {
    console.log(`Exporting risks as ${format.toUpperCase()}`);
    // TODO: Implement actual export logic
  };

  // Mock data for visualizations
  const heatMapData = [
    { category: 'Operational', department: 'Technology', count: 23, severity: 'high' as const },
    { category: 'Operational', department: 'Finance', count: 8, severity: 'medium' as const },
    { category: 'Operational', department: 'Operations', count: 15, severity: 'high' as const },
    { category: 'Operational', department: 'Compliance', count: 5, severity: 'low' as const },
    { category: 'Operational', department: 'HR', count: 7, severity: 'medium' as const },
    
    { category: 'Financial', department: 'Technology', count: 4, severity: 'medium' as const },
    { category: 'Financial', department: 'Finance', count: 18, severity: 'critical' as const },
    { category: 'Financial', department: 'Operations', count: 12, severity: 'high' as const },
    { category: 'Financial', department: 'Compliance', count: 9, severity: 'medium' as const },
    { category: 'Financial', department: 'HR', count: 2, severity: 'low' as const },
    
    { category: 'Strategic', department: 'Technology', count: 11, severity: 'high' as const },
    { category: 'Strategic', department: 'Finance', count: 14, severity: 'high' as const },
    { category: 'Strategic', department: 'Operations', count: 6, severity: 'medium' as const },
    { category: 'Strategic', department: 'Compliance', count: 3, severity: 'low' as const },
    { category: 'Strategic', department: 'HR', count: 8, severity: 'medium' as const },
    
    { category: 'Compliance', department: 'Technology', count: 16, severity: 'critical' as const },
    { category: 'Compliance', department: 'Finance', count: 12, severity: 'high' as const },
    { category: 'Compliance', department: 'Operations', count: 7, severity: 'medium' as const },
    { category: 'Compliance', department: 'Compliance', count: 22, severity: 'critical' as const },
    { category: 'Compliance', department: 'HR', count: 5, severity: 'medium' as const },
    
    { category: 'Reputational', department: 'Technology', count: 9, severity: 'medium' as const },
    { category: 'Reputational', department: 'Finance', count: 6, severity: 'low' as const },
    { category: 'Reputational', department: 'Operations', count: 11, severity: 'high' as const },
    { category: 'Reputational', department: 'Compliance', count: 4, severity: 'low' as const },
    { category: 'Reputational', department: 'HR', count: 13, severity: 'high' as const },
  ];

  const matrixRisks = [
    { id: 'R1', name: 'Data Breach', likelihood: 4, impact: 5, category: 'Operational' },
    { id: 'R2', name: 'DORA Non-compliance', likelihood: 3, impact: 5, category: 'Compliance' },
    { id: 'R3', name: 'Key Personnel Loss', likelihood: 3, impact: 4, category: 'Operational' },
    { id: 'R4', name: 'Cloud Outage', likelihood: 2, impact: 4, category: 'Operational' },
    { id: 'R5', name: 'Market Volatility', likelihood: 5, impact: 3, category: 'Financial' },
    { id: 'R6', name: 'Supplier Failure', likelihood: 2, impact: 3, category: 'Operational' },
    { id: 'R7', name: 'Regulatory Change', likelihood: 4, impact: 3, category: 'Compliance' },
    { id: 'R8', name: 'System Integration', likelihood: 3, impact: 2, category: 'Operational' },
    { id: 'R9', name: 'Brand Damage', likelihood: 2, impact: 5, category: 'Reputational' },
    { id: 'R10', name: 'Budget Overrun', likelihood: 4, impact: 2, category: 'Financial' },
    { id: 'R11', name: 'Strategic Misalignment', likelihood: 2, impact: 4, category: 'Strategic' },
    { id: 'R12', name: 'Audit Findings', likelihood: 3, impact: 3, category: 'Compliance' },
  ];

  const dependencyNodes = [
    { id: 'R1', name: 'Data Breach', severity: 'critical' as const },
    { id: 'R2', name: 'DORA Compliance', severity: 'critical' as const },
    { id: 'R3', name: 'Key Personnel', severity: 'high' as const },
    { id: 'R4', name: 'Cloud Outage', severity: 'high' as const },
    { id: 'R5', name: 'Market Risk', severity: 'medium' as const },
    { id: 'R6', name: 'Supplier Risk', severity: 'medium' as const },
    { id: 'R7', name: 'Brand Damage', severity: 'high' as const },
    { id: 'R8', name: 'System Integration', severity: 'medium' as const },
  ];

  const dependencies = [
    { source: 'R1', target: 'R7', type: 'triggers' as const },
    { source: 'R1', target: 'R2', type: 'triggers' as const },
    { source: 'R4', target: 'R1', type: 'triggers' as const },
    { source: 'R3', target: 'R4', type: 'related' as const },
    { source: 'R6', target: 'R4', type: 'triggers' as const },
    { source: 'R2', target: 'R7', type: 'triggers' as const },
    { source: 'R8', target: 'R4', type: 'mitigates' as const },
    { source: 'R5', target: 'R7', type: 'triggers' as const },
    { source: 'R3', target: 'R8', type: 'related' as const },
  ];

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="risk-register-main"
        icon={Shield}
        title="Risk Register Coming Soon"
        description="The comprehensive risk register with advanced filtering, sorting, and risk matrix visualization will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Risk Register</h1>
          <p className="text-neutral-400 mt-1">
            Monitor and manage organizational risks
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tier Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value as RiskTier)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none pr-10"
            >
              <option value="all">All Tiers</option>
              <option value="1">Tier 1 Only</option>
              <option value="2">Tier 2 Only</option>
              <option value="3">Tier 3 Only</option>
            </select>
            <Layers className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          </div>
          
          <ExportButton onExport={handleExport} />
          <button className="btn-primary">
            Add New Risk
          </button>
        </div>
      </div>

      {/* Filter Chips */}
      {activeFilters.length > 0 && (
        <FilterChips
          filters={activeFilters}
          onRemove={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      {/* View Mode Tabs */}
      <div className="flex items-center gap-2 border-b border-neutral-800">
        <button
          onClick={() => setViewMode('table')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2",
            viewMode === 'table'
              ? "text-primary-400 border-primary-400"
              : "text-neutral-400 border-transparent hover:text-neutral-300"
          )}
        >
          <LayoutGrid className="w-4 h-4" />
          <span>Table View</span>
        </button>
        <button
          onClick={() => setViewMode('heatmap')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2",
            viewMode === 'heatmap'
              ? "text-primary-400 border-primary-400"
              : "text-neutral-400 border-transparent hover:text-neutral-300"
          )}
        >
          <LayoutGrid className="w-4 h-4" />
          <span>Heat Map</span>
        </button>
        <button
          onClick={() => setViewMode('matrix')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2",
            viewMode === 'matrix'
              ? "text-primary-400 border-primary-400"
              : "text-neutral-400 border-transparent hover:text-neutral-300"
          )}
        >
          <ScatterChart className="w-4 h-4" />
          <span>L×I Matrix</span>
        </button>
        <button
          onClick={() => setViewMode('dependency')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2",
            viewMode === 'dependency'
              ? "text-primary-400 border-primary-400"
              : "text-neutral-400 border-transparent hover:text-neutral-300"
          )}
        >
          <Network className="w-4 h-4" />
          <span>Dependency Map</span>
        </button>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Risks</p>
              <p className="text-2xl font-bold text-white mt-1">127</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Shield className="text-primary-400" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm">
            <TrendingUp className="text-success-500 mr-1" size={16} />
            <span className="text-success-500">+12%</span>
            <span className="text-neutral-500 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Critical Risks</p>
              <p className="text-2xl font-bold text-danger-500 mt-1">8</p>
            </div>
            <div className="p-3 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="text-danger-500" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm">
            <TrendingDown className="text-success-500 mr-1" size={16} />
            <span className="text-success-500">-2</span>
            <span className="text-neutral-500 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">High Risks</p>
              <p className="text-2xl font-bold text-warning-500 mt-1">23</p>
            </div>
            <div className="p-3 bg-warning-500/20 rounded-lg">
              <Shield className="text-warning-500" size={24} />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm">
            <TrendingUp className="text-danger-500 mr-1" size={16} />
            <span className="text-danger-500">+3</span>
            <span className="text-neutral-500 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-neutral-400">Risk Tiers</p>
              <p className="text-2xl font-bold text-white mt-1">3</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Layers className="text-primary-400" size={24} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-400">Tier 1</span>
              <span className="font-semibold text-red-400">12</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-400">Tier 2</span>
              <span className="font-semibold text-orange-400">45</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-400">Tier 3</span>
              <span className="font-semibold text-yellow-400">70</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Visualizations based on view mode */}
      {viewMode === 'heatmap' && (
        <RiskHeatMap data={heatMapData} />
      )}

      {viewMode === 'matrix' && (
        <LikelihoodImpactMatrix risks={matrixRisks} />
      )}

      {viewMode === 'dependency' && (
        <RiskDependencyMap nodes={dependencyNodes} dependencies={dependencies} />
      )}

      {/* Risk Table - Only show in table view */}
      {viewMode === 'table' && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Risks</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Risk Title</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Tier</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Severity</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Owner</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                  <td className="py-3 px-4 text-sm font-mono text-primary-400">RISK-001</td>
                  <td className="py-3 px-4 text-sm text-white">Data breach from third-party vendor</td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Cybersecurity</td>
                  <td className="py-3 px-4"><span className="px-2 py-1 text-xs font-semibold rounded bg-red-600/20 text-red-400 border border-red-600/30">Tier 1</span></td>
                  <td className="py-3 px-4"><span className="badge-danger">Critical</span></td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Sarah Johnson</td>
                  <td className="py-3 px-4"><span className="badge-warning">Open</span></td>
                </tr>
                <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                  <td className="py-3 px-4 text-sm font-mono text-primary-400">RISK-002</td>
                  <td className="py-3 px-4 text-sm text-white">Regulatory compliance failure (DORA)</td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Compliance</td>
                  <td className="py-3 px-4"><span className="px-2 py-1 text-xs font-semibold rounded bg-red-600/20 text-red-400 border border-red-600/30">Tier 1</span></td>
                  <td className="py-3 px-4"><span className="badge-danger">Critical</span></td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Michael Chen</td>
                  <td className="py-3 px-4"><span className="badge-primary">Mitigated</span></td>
                </tr>
                <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                  <td className="py-3 px-4 text-sm font-mono text-primary-400">RISK-003</td>
                  <td className="py-3 px-4 text-sm text-white">Key personnel dependency in ops team</td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Operational</td>
                  <td className="py-3 px-4"><span className="px-2 py-1 text-xs font-semibold rounded bg-orange-600/20 text-orange-400 border border-orange-600/30">Tier 2</span></td>
                  <td className="py-3 px-4"><span className="badge-warning">High</span></td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Emily Rodriguez</td>
                  <td className="py-3 px-4"><span className="badge-warning">Open</span></td>
                </tr>
                <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                  <td className="py-3 px-4 text-sm font-mono text-primary-400">RISK-004</td>
                  <td className="py-3 px-4 text-sm text-white">Cloud infrastructure outage</td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Technology</td>
                  <td className="py-3 px-4"><span className="px-2 py-1 text-xs font-semibold rounded bg-orange-600/20 text-orange-400 border border-orange-600/30">Tier 2</span></td>
                  <td className="py-3 px-4"><span className="badge-warning">High</span></td>
                  <td className="py-3 px-4 text-sm text-neutral-300">David Kim</td>
                  <td className="py-3 px-4"><span className="badge-primary">Mitigated</span></td>
                </tr>
                <tr className="hover:bg-neutral-900/50">
                  <td className="py-3 px-4 text-sm font-mono text-primary-400">RISK-005</td>
                  <td className="py-3 px-4 text-sm text-white">Market volatility affecting liquidity</td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Financial</td>
                  <td className="py-3 px-4"><span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-600/20 text-yellow-400 border border-yellow-600/30">Tier 3</span></td>
                  <td className="py-3 px-4"><span className="badge-primary">Medium</span></td>
                  <td className="py-3 px-4 text-sm text-neutral-300">Jessica Park</td>
                  <td className="py-3 px-4"><span className="badge-warning">Open</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
